import { type FC, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tab, Nav } from 'react-bootstrap';

import { type RootState } from '@editor2/reducers/root';
import { fetchPageWithElementsData } from '@editor2/reducers/editor/effects';
import { pageElementsPage } from '@editor2/containers/PageElementsPage/styles';
import {
	checkUnsavedTocChanges,
	checkUnsavedConfigChanges
} from '@editor2/services/unsavedChangesService';
import Header from '@editor2/components/Header';
import UnsavedChangesTooltip from '@editor2/components/UnsavedChangesTooltip';
import PageEditorForm from './components/PageEditorForm';
import PageSettings from './components/PageSettings';
import TableOfContents from './components/TableOfContents';
import { updateActiveTab } from '@editor2/reducers/editor/editorSlice';
import { useConnectSocket } from '@editor2/hooks/useConnectSocket';
import { useTabTitle } from '@editor2/hooks/useTabTitle';
import LoaderWrapper from '@editor2/components/LoaderWrapper';
import { sanitizeCourseId } from '@editor2/services/helpersService';
import { ChangesStatus } from '@editor2/types/changesStatus';
import Navigation from '@editor2/components/Header/Navigation';
import RoutingCheckWrapper from '../App/RoutingCheckWrapper';
import { useSettingsMode } from '@editor2/hooks/useSettingsMode';
import { checkTypeIsSet } from '@editor2/services/configService';
import SideOptionsPanel from '../App/SideOptionsPanel';

interface Props {
	match: {
		params: {
			courseId: string;
			chapterId?: string;
			pageGroupId: string;
			pageId: string;
		};
	};
}

const PageElementsPage: FC<Props> = (props) => {
	const { match } = props;
	const { courseId: courseIdParam, chapterId, pageGroupId, pageId } = match.params;
	const courseId = sanitizeCourseId(courseIdParam);

	const dispatch = useDispatch();

	const {
		course,
		headCommit,
		chapters,
		page_groups,
		pages,
		changes,
		settingsMode,
		activeTab,
		config
	} = useSelector((state: RootState) => state.courses[courseId]);

	const chapter = chapters && chapters.entities[chapterId];
	const pageGroup = page_groups && page_groups.entities[pageGroupId];
	const page = pages && pages.entities[pageId];

	const [key, setKey] = useState(activeTab.elements || 'toc');

	useConnectSocket({ roomName: `page.${pageId}`, courseId });
	useTabTitle({ title: `${course.name}: ${page.name}` });

	const onTabChange = (key: string) => {
		setKey(key);
		dispatch(updateActiveTab({ courseId, key: 'elements', activeTab: key }));
	};

	useEffect(() => {
		if (checkUnsavedConfigChanges(changes) || !checkTypeIsSet(config.page_types, page.page_type))
			setKey('settings');
		if (key === 'settings' && checkUnsavedTocChanges(changes) && !settingsMode) setKey('toc');
	}, [changes]);

	useSettingsMode({ settingsMode, setKey });

	useEffect(() => {
		const isNew = changes.pages[page.family_id]?.includes(ChangesStatus.New);
		const elementsFetched = page.elements;

		if (!isNew && !elementsFetched) {
			dispatch(
				fetchPageWithElementsData({
					courseId,
					bookId: headCommit.thing_id,
					pageId: page.id
				})
			);
		}
	}, [page]);

	return (
		<RoutingCheckWrapper courseId={courseId}>
			<div className={pageElementsPage}>
				<Header course={course} chapter={chapter} pageGroup={pageGroup} page={page} />
				<SideOptionsPanel courseId={courseId} />
				<Navigation courseId={courseId} chapter={chapter} pageGroup={pageGroup} page={page} />

				<LoaderWrapper loading={!page || !page.elements}>
					<Tab.Container id="" activeKey={key} onSelect={(k) => onTabChange(k)}>
						<Nav variant="tabs" className="tocTabs">
							<UnsavedChangesTooltip condition={checkUnsavedConfigChanges(changes) || settingsMode}>
								<Nav.Item>
									<Nav.Link
										eventKey="page"
										disabled={
											checkUnsavedConfigChanges(changes) ||
											!checkTypeIsSet(config.page_types, page.page_type) ||
											settingsMode
										}>
										Properties
									</Nav.Link>
								</Nav.Item>
							</UnsavedChangesTooltip>

							<UnsavedChangesTooltip condition={checkUnsavedConfigChanges(changes) || settingsMode}>
								<Nav.Item>
									<Nav.Link
										eventKey="toc"
										disabled={
											checkUnsavedConfigChanges(changes) ||
											!checkTypeIsSet(config.page_types, page.page_type) ||
											settingsMode
										}>
										Contents
									</Nav.Link>
								</Nav.Item>
							</UnsavedChangesTooltip>

							<UnsavedChangesTooltip condition={checkUnsavedTocChanges(changes) && !settingsMode}>
								<Nav.Item>
									<Nav.Link
										eventKey="settings"
										disabled={checkUnsavedTocChanges(changes) && !settingsMode}>
										Settings
									</Nav.Link>
								</Nav.Item>
							</UnsavedChangesTooltip>
						</Nav>

						<Tab.Content>
							<Tab.Pane eventKey="page">
								<PageEditorForm
									page={page}
									chapterId={chapterId}
									courseId={courseId}
									key={pageGroupId}
								/>
							</Tab.Pane>

							<Tab.Pane eventKey="toc">
								<TableOfContents courseId={courseId} pageId={pageId} />
							</Tab.Pane>

							<Tab.Pane eventKey="settings">
								<PageSettings courseId={courseId} page={page} />
							</Tab.Pane>
						</Tab.Content>
					</Tab.Container>
				</LoaderWrapper>
			</div>
		</RoutingCheckWrapper>
	);
};

export default PageElementsPage;
